import React, { useState, useEffect, useRef } from 'react';
import {
    MessageSquare, Search, Plus, Users, User, Send, Paperclip,
    MoreVertical, Edit2, Trash2, X, Check, ChevronLeft,
    UserPlus, Settings, Clock, CheckCheck, Download, Loader2, FileText, ExternalLink, Eye
} from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AdminMessagesPage = () => {
    const [activeTab, setActiveTab] = useState('onboarding');
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [sending, setSending] = useState(false);

    // Modal states
    const [showNewConversation, setShowNewConversation] = useState(false);
    const [showGroupSettings, setShowGroupSettings] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [isGroup, setIsGroup] = useState(false);
    const [groupName, setGroupName] = useState('');

    // Edit message state
    const [editingMessage, setEditingMessage] = useState(null);
    const [editContent, setEditContent] = useState('');

    // File attachment state
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [previewFile, setPreviewFile] = useState(null); // For in-app file viewer
    const fileInputRef = useRef(null);
    const messagesEndRef = useRef(null);
    const messageContainerRef = useRef(null);
    const shouldScrollRef = useRef(true); // Track if we should auto-scroll

    useEffect(() => {
        fetchConversations();

        // Set up real-time polling for conversations list
        const conversationInterval = setInterval(() => {
            fetchConversations(true); // Silent refresh
        }, 5000); // Poll every 5 seconds

        return () => clearInterval(conversationInterval);
    }, [activeTab]);

    useEffect(() => {
        if (selectedConversation) {
            fetchMessages(selectedConversation.id);

            // Set up real-time polling for messages
            const messageInterval = setInterval(() => {
                fetchMessages(selectedConversation.id, true); // Silent refresh
            }, 3000); // Poll every 3 seconds

            return () => clearInterval(messageInterval);
        }
    }, [selectedConversation]);

    useEffect(() => {
        // Only scroll to bottom if shouldScrollRef is true
        if (shouldScrollRef.current) {
            scrollToBottom();
        }
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Handle scroll event to detect if user scrolled up
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        shouldScrollRef.current = isNearBottom;
    };

    const fetchConversations = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await api.get('/messages/admin/conversations');
            setConversations(response.data.conversations || []);
        } catch (error) {
            console.error('Error fetching conversations:', error);
            if (!silent) toast.error('Failed to load conversations');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const fetchMessages = async (conversationId, silent = false) => {
        if (!silent) setMessagesLoading(true);
        try {
            const response = await api.get(`/messages/conversations/${conversationId}/messages`);
            setMessages(response.data.messages || []);
        } catch (error) {
            console.error('Error fetching messages:', error);
            if (!silent) toast.error('Failed to load messages');
        } finally {
            if (!silent) setMessagesLoading(false);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await api.get('/messages/admin/employees');
            setEmployees(response.data.employees || []);
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const handleNewConversation = () => {
        setShowNewConversation(true);
        setSelectedEmployees([]);
        setIsGroup(false);
        setGroupName('');
        fetchEmployees();
    };

    const createConversation = async () => {
        if (selectedEmployees.length === 0) {
            toast.error('Please select at least one employee');
            return;
        }

        if (isGroup && !groupName.trim()) {
            toast.error('Please enter a group name');
            return;
        }

        try {
            const response = await api.post('/messages/admin/conversations', {
                name: isGroup ? groupName : null,
                is_group: isGroup,
                // phase: activeTab, // Removed phase
                employee_ids: selectedEmployees.map(e => e.id)
            });

            if (response.data.existing) {
                toast.success('Opening existing conversation');
            } else {
                toast.success('Conversation created');
            }

            setShowNewConversation(false);
            fetchConversations();

            // Select the new/existing conversation
            const convId = response.data.conversation.id;
            const conv = conversations.find(c => c.id === convId) || response.data.conversation;
            setSelectedConversation(conv);
        } catch (error) {
            console.error('Error creating conversation:', error);
            toast.error('Failed to create conversation');
        }
    };

    const sendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() && attachments.length === 0) return;
        if (!selectedConversation) return;

        const messageContent = newMessage;
        const messageAttachments = [...attachments];

        // Optimistically add message to UI
        const tempMessage = {
            id: `temp-${Date.now()}`,
            content: messageContent,
            attachments: messageAttachments.length > 0 ? messageAttachments : null,
            sender_type: 'admin',
            created_at: new Date().toISOString(),
            sending: true
        };

        setMessages([...messages, tempMessage]);
        setNewMessage('');
        setAttachments([]);
        setSending(true);

        try {
            const response = await api.post(`/messages/conversations/${selectedConversation.id}/messages`, {
                content: messageContent,
                attachments: messageAttachments.length > 0 ? messageAttachments : null
            });

            // Replace temp message with real one
            setMessages(prev => prev.map(m =>
                m.id === tempMessage.id ? response.data.message : m
            ));
            fetchConversations(true); // Update last message in list
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error('Failed to send message');
            // Remove temp message on error
            setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
        } finally {
            setSending(false);
        }
    };

    const handleEditMessage = async (messageId) => {
        if (!editContent.trim()) return;

        try {
            await api.put(`/messages/messages/${messageId}`, {
                content: editContent
            });

            setMessages(messages.map(m =>
                m.id === messageId ? { ...m, content: editContent, is_edited: true } : m
            ));
            setEditingMessage(null);
            setEditContent('');
            toast.success('Message updated');
        } catch (error) {
            console.error('Error editing message:', error);
            toast.error('Failed to edit message');
        }
    };

    const handleDeleteMessage = async (messageId) => {
        if (!window.confirm('Are you sure you want to delete this message?')) return;

        try {
            await api.delete(`/messages/admin/messages/${messageId}`);
            setMessages(messages.filter(m => m.id !== messageId));
            toast.success('Message deleted');
        } catch (error) {
            console.error('Error deleting message:', error);
            toast.error('Failed to delete message');
        }
    };

    const handleDeleteConversation = async () => {
        if (!selectedConversation) return;
        if (!window.confirm('Are you sure you want to delete this conversation? All messages will be lost.')) return;

        try {
            await api.delete(`/messages/admin/conversations/${selectedConversation.id}`);
            setSelectedConversation(null);
            setMessages([]);
            fetchConversations();
            toast.success('Conversation deleted');
        } catch (error) {
            console.error('Error deleting conversation:', error);
            toast.error('Failed to delete conversation');
        }
    };

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        setUploading(true);

        try {
            const formData = new FormData();
            files.forEach(file => formData.append('files', file));

            const response = await api.post('/messages/upload-attachment', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.success && response.data.files) {
                setAttachments(prev => [...prev, ...response.data.files]);
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            toast.error('Failed to upload files');
        } finally {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const removeAttachment = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Yesterday';
        } else if (diffDays < 7) {
            return date.toLocaleDateString('en-US', { weekday: 'short' });
        } else {
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }
    };

    const getParticipantName = (conversation) => {
        if (conversation.is_group) return conversation.name;
        const employee = conversation.participants?.find(p => p.is_admin === false || p.is_admin === null);
        console.log('getParticipantName - conversation:', conversation.id, 'participants:', conversation.participants, 'found employee:', employee);
        if (!employee) return 'Unknown';
        return employee.name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown';
    };

    const getParticipantInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const filteredConversations = conversations.filter(conv => {
        const name = getParticipantName(conv);
        return name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    return (
        <div className="h-[calc(100vh-4rem)] flex flex-col">
            {/* Header */}
            <div className="bg-card border-b border-border px-6 py-4">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Messages</h1>
                        <p className="text-muted-foreground text-sm">Communicate with your employees</p>
                    </div>
                </div>

                {/* Tabs */}
                {/* <div className="border-b border-border">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => {
                                setActiveTab('onboarding');
                                setSelectedConversation(null);
                                setMessages([]);
                            }}
                            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'onboarding'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                }`}
                        >
                            Onboarding
                        </button>
                        <button
                            onClick={() => {
                                setActiveTab('preboarding');
                                setSelectedConversation(null);
                                setMessages([]);
                            }}
                            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'preboarding'
                                ? 'border-primary text-primary'
                                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                                }`}
                        >
                            Preboarding
                        </button>
                    </nav>
                </div> */}
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden bg-background">
                {/* Conversations List */}
                <div className={`w-80 bg-card border-r border-border flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'
                    }`}>
                    {/* Search & New */}
                    <div className="p-4 border-b border-border">
                        <div className="flex gap-2">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search conversations..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-input rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                />
                            </div>
                            <button
                                onClick={handleNewConversation}
                                className="p-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
                                title="New Conversation"
                            >
                                <Plus className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Conversations */}
                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center h-32">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                            </div>
                        ) : filteredConversations.length === 0 ? (
                            <div className="p-6 text-center">
                                <MessageSquare className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                                <p className="text-muted-foreground text-sm">No conversations yet</p>
                                <button
                                    onClick={handleNewConversation}
                                    className="mt-3 text-primary text-sm font-medium hover:underline"
                                >
                                    Start a conversation
                                </button>
                            </div>
                        ) : (
                            filteredConversations.map((conv) => (
                                <button
                                    key={conv.id}
                                    onClick={() => setSelectedConversation(conv)}
                                    className={`w-full p-4 flex items-start gap-3 hover:bg-muted/50 transition-colors border-b border-border ${selectedConversation?.id === conv.id ? 'bg-primary/5' : ''
                                        }`}
                                >
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm ${conv.is_group ? 'bg-secondary text-secondary-foreground' : 'bg-primary text-primary-foreground'
                                        }`}>
                                        {conv.is_group ? (
                                            <Users className="w-5 h-5" />
                                        ) : (
                                            getParticipantInitials(getParticipantName(conv))
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0 text-left">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-medium text-foreground truncate">
                                                {getParticipantName(conv)}
                                            </h4>
                                            {conv.last_message && (
                                                <span className="text-xs text-muted-foreground">
                                                    {formatTime(conv.last_message.created_at)}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-muted-foreground truncate">
                                            {conv.last_message?.content || 'No messages yet'}
                                        </p>
                                        {conv.unread_count > 0 && (
                                            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-medium text-primary-foreground bg-primary rounded-full mt-1">
                                                {conv.unread_count}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                </div>

                {/* Chat Area */}
                <div className={`flex-1 flex flex-col ${!selectedConversation ? 'hidden md:flex' : 'flex'
                    }`}>
                    {selectedConversation ? (
                        <>
                            {/* Chat Header */}
                            <div className="bg-card border-b border-border px-4 py-3 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setSelectedConversation(null)}
                                        className="md:hidden p-1 text-muted-foreground hover:bg-muted rounded"
                                    >
                                        <ChevronLeft className="w-5 h-5" />
                                    </button>
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${selectedConversation.is_group ? 'bg-secondary text-secondary-foreground' : 'bg-primary text-primary-foreground'
                                        }`}>
                                        {selectedConversation.is_group ? (
                                            <Users className="w-5 h-5" />
                                        ) : (
                                            getParticipantInitials(getParticipantName(selectedConversation))
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-foreground">
                                            {getParticipantName(selectedConversation)}
                                        </h3>
                                        <p className="text-xs text-muted-foreground">
                                            {selectedConversation.is_group
                                                ? `${selectedConversation.participants?.filter(p => p.type === 'employee').length || 0} members`
                                                : selectedConversation.participants?.find(p => p.type === 'employee')?.email
                                            }
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {selectedConversation.is_group && (
                                        <button
                                            onClick={() => setShowGroupSettings(true)}
                                            className="p-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                                            title="Group Settings"
                                        >
                                            <Settings className="w-5 h-5" />
                                        </button>
                                    )}
                                    <button
                                        onClick={handleDeleteConversation}
                                        className="p-2 text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                                        title="Delete Conversation"
                                    >
                                        <Trash2 className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Messages */}
                            <div
                                ref={messageContainerRef}
                                onScroll={handleScroll}
                                className="flex-1 overflow-y-auto p-4 space-y-4"
                            >
                                {messagesLoading ? (
                                    <div className="flex items-center justify-center h-32">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                                    </div>
                                ) : messages.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                                        <MessageSquare className="w-16 h-16 text-muted-foreground/30 mb-4" />
                                        <p>No messages yet</p>
                                        <p className="text-sm">Send a message to start the conversation</p>
                                    </div>
                                ) : (
                                    messages.map((message) => {
                                        const isAdmin = message.sender_type === 'admin';
                                        const isEditing = editingMessage === message.id;

                                        return (
                                            <div
                                                key={message.id}
                                                className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} animate-fadeIn`}
                                            >
                                                <div className={`max-w-[70%]`}>
                                                    {!isAdmin && (
                                                        <p className="text-xs font-medium text-muted-foreground mb-1 ml-1">
                                                            {message.sender_name}
                                                        </p>
                                                    )}
                                                    <div className={`group relative rounded-2xl px-4 py-3 shadow-sm ${isAdmin
                                                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                                                        : 'bg-card border-2 border-border text-foreground rounded-bl-sm'
                                                        }`}>
                                                        {isEditing ? (
                                                            <div className="flex items-center gap-2">
                                                                <input
                                                                    type="text"
                                                                    value={editContent}
                                                                    onChange={(e) => setEditContent(e.target.value)}
                                                                    className="flex-1 bg-transparent border-b border-white/50 focus:outline-none text-sm"
                                                                    autoFocus
                                                                />
                                                                <button
                                                                    onClick={() => handleEditMessage(message.id)}
                                                                    className="p-1 hover:bg-white/20 rounded"
                                                                >
                                                                    <Check className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingMessage(null);
                                                                        setEditContent('');
                                                                    }}
                                                                    className="p-1 hover:bg-white/20 rounded"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <>
                                                                <p className={`text-sm whitespace-pre-wrap leading-relaxed ${isAdmin ? 'text-primary-foreground' : 'text-foreground'
                                                                    }`}>
                                                                    {message.content}
                                                                </p>
                                                                {message.attachments && message.attachments.length > 0 && (
                                                                    <div className="mt-3 space-y-2">
                                                                        {message.attachments.map((file, idx) => {
                                                                            // Format file size
                                                                            const formatSize = (bytes) => {
                                                                                if (!bytes) return '';
                                                                                if (bytes < 1024) return `${bytes} B`;
                                                                                if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                                                                                return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                                                                            };

                                                                            // Get file extension
                                                                            const ext = file.file_name?.split('.').pop()?.toUpperCase() || 'FILE';

                                                                            // Check if file is previewable
                                                                            const isPreviewable = /\.(pdf|png|jpg|jpeg|gif|webp|svg|mp4|webm|mp3|wav)$/i.test(file.file_name || '');

                                                                            return (
                                                                                <div
                                                                                    key={idx}
                                                                                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isAdmin
                                                                                        ? 'bg-primary-foreground/10 border border-primary-foreground/20'
                                                                                        : 'bg-muted/50 border border-border'
                                                                                        }`}
                                                                                >
                                                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isAdmin ? 'bg-primary-foreground/20' : 'bg-primary/10'}`}>
                                                                                        <FileText className={`w-5 h-5 ${isAdmin ? 'text-primary-foreground' : 'text-primary'}`} />
                                                                                    </div>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <p className={`text-sm font-medium truncate ${isAdmin ? 'text-primary-foreground' : 'text-foreground'}`}>
                                                                                            {file.file_name}
                                                                                        </p>
                                                                                        <p className={`text-xs ${isAdmin ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                                                                                            {ext} • {formatSize(file.file_size)}
                                                                                        </p>
                                                                                    </div>
                                                                                    <div className="flex items-center gap-1">
                                                                                        {isPreviewable && (
                                                                                            <button
                                                                                                onClick={() => setPreviewFile(file)}
                                                                                                className={`p-2 rounded-lg transition-colors ${isAdmin
                                                                                                    ? 'hover:bg-primary-foreground/20 text-primary-foreground/80 hover:text-primary-foreground'
                                                                                                    : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                                                                                    }`}
                                                                                                title="Preview"
                                                                                            >
                                                                                                <Eye className="w-4 h-4" />
                                                                                            </button>
                                                                                        )}
                                                                                        <a
                                                                                            href={file.file_url}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            className={`p-2 rounded-lg transition-colors ${isAdmin
                                                                                                ? 'hover:bg-primary-foreground/20 text-primary-foreground/80 hover:text-primary-foreground'
                                                                                                : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                                                                                }`}
                                                                                            title="Open in new tab"
                                                                                        >
                                                                                            <ExternalLink className="w-4 h-4" />
                                                                                        </a>
                                                                                    </div>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                        {/* Message actions */}
                                                        {isAdmin && !isEditing && (
                                                            <div className="absolute -left-16 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                                                                <button
                                                                    onClick={() => {
                                                                        setEditingMessage(message.id);
                                                                        setEditContent(message.content);
                                                                    }}
                                                                    className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded"
                                                                    title="Edit"
                                                                >
                                                                    <Edit2 className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleDeleteMessage(message.id)}
                                                                    className="p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded"
                                                                    title="Delete"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={`flex items-center gap-1 mt-1 text-xs ${isAdmin ? 'justify-end text-muted-foreground' : 'text-muted-foreground'
                                                        }`}>
                                                        <Clock className="w-3 h-3" />
                                                        <span>{formatTime(message.created_at)}</span>
                                                        {message.is_edited && <span className="italic">(edited)</span>}
                                                        {message.sending && (
                                                            <span className="flex items-center gap-1 text-muted-foreground italic">
                                                                <div className="w-1 h-1 bg-muted-foreground rounded-full animate-pulse"></div>
                                                                Sending...
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Attachments Preview */}
                            {attachments.length > 0 && (
                                <div className="bg-muted/30 border-t border-border px-4 py-2">
                                    <div className="flex flex-wrap gap-2">
                                        {attachments.map((file, index) => (
                                            <div
                                                key={index}
                                                className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-1.5 text-sm"
                                            >
                                                <Paperclip className="w-4 h-4 text-muted-foreground" />
                                                <span className="truncate max-w-[150px]">{file.file_name}</span>
                                                <button
                                                    onClick={() => removeAttachment(index)}
                                                    className="p-0.5 text-muted-foreground hover:text-destructive"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Message Input */}
                            <form onSubmit={sendMessage} className="bg-card border-t border-border p-4">
                                <div className="flex items-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={uploading}
                                        className="p-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors disabled:opacity-50"
                                        title="Attach file"
                                    >
                                        {uploading ? (
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                        ) : (
                                            <Paperclip className="w-5 h-5" />
                                        )}
                                    </button>
                                    <input
                                        type="file"
                                        ref={fileInputRef}
                                        onChange={handleFileSelect}
                                        multiple
                                        className="hidden"
                                    />
                                    <div className="flex-1">
                                        <textarea
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            placeholder="Type a message..."
                                            rows={1}
                                            className="w-full px-4 py-2 bg-background border border-input rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    sendMessage(e);
                                                }
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={sending || (!newMessage.trim() && attachments.length === 0)}
                                        className="p-3 bg-primary text-white rounded-xl hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {sending ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <Send className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground">
                            <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
                                <MessageSquare className="w-12 h-12 text-muted-foreground/30" />
                            </div>
                            <h3 className="text-lg font-medium text-foreground mb-1">Your Messages</h3>
                            <p className="text-sm text-center max-w-sm">
                                Select a conversation or start a new one to message your employees
                            </p>
                            <button
                                onClick={handleNewConversation}
                                className="mt-4 flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                New Conversation
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* New Conversation Modal */}
            {
                showNewConversation && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-card rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
                            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">New Conversation</h3>
                                <button
                                    onClick={() => setShowNewConversation(false)}
                                    className="p-1 text-muted-foreground hover:bg-muted rounded-full"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 space-y-4">
                                {/* Conversation Type Toggle */}
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => setIsGroup(false)}
                                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${!isGroup
                                            ? 'border-primary bg-primary/5 text-primary'
                                            : 'border-border text-muted-foreground hover:border-muted-foreground/50'
                                            }`}
                                    >
                                        <User className="w-5 h-5 mx-auto mb-1" />
                                        <span className="text-sm font-medium">Direct Message</span>
                                    </button>
                                    <button
                                        onClick={() => setIsGroup(true)}
                                        className={`flex-1 py-2 px-4 rounded-lg border-2 transition-colors ${isGroup
                                            ? 'border-primary bg-primary/5 text-primary'
                                            : 'border-border text-muted-foreground hover:border-muted-foreground/50'
                                            }`}
                                    >
                                        <Users className="w-5 h-5 mx-auto mb-1" />
                                        <span className="text-sm font-medium">Group Chat</span>
                                    </button>
                                </div>

                                {/* Group Name Input */}
                                {isGroup && (
                                    <div>
                                        <label className="block text-sm font-medium text-muted-foreground mb-1">
                                            Group Name
                                        </label>
                                        <input
                                            type="text"
                                            value={groupName}
                                            onChange={(e) => setGroupName(e.target.value)}
                                            placeholder="Enter group name..."
                                            className="w-full px-3 py-2 bg-background border border-input rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                                        />
                                    </div>
                                )}

                                {/* Employee Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                                        Select {isGroup ? 'Participants' : 'Employee'}
                                    </label>
                                    <div className="border border-border rounded-lg max-h-60 overflow-y-auto">
                                        {employees.length === 0 ? (
                                            <p className="p-4 text-center text-muted-foreground text-sm">
                                                No {activeTab} employees found
                                            </p>
                                        ) : (
                                            employees.map((emp) => {
                                                const isSelected = selectedEmployees.some(e => e.id === emp.id);
                                                return (
                                                    <button
                                                        key={emp.id}
                                                        onClick={() => {
                                                            if (isGroup) {
                                                                if (isSelected) {
                                                                    setSelectedEmployees(selectedEmployees.filter(e => e.id !== emp.id));
                                                                } else {
                                                                    setSelectedEmployees([...selectedEmployees, emp]);
                                                                }
                                                            } else {
                                                                setSelectedEmployees([emp]);
                                                            }
                                                        }}
                                                        className={`w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors ${isSelected ? 'bg-primary/5' : ''
                                                            }`}
                                                    >
                                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${isSelected ? 'bg-primary' : 'bg-muted-foreground'
                                                            }`}>
                                                            {emp.first_name[0]}{emp.last_name[0]}
                                                        </div>
                                                        <div className="flex-1 text-left">
                                                            <p className="font-medium text-foreground">
                                                                {emp.first_name} {emp.last_name}
                                                            </p>
                                                            <p className="text-xs text-muted-foreground">{emp.email}</p>
                                                        </div>
                                                        {isSelected && (
                                                            <Check className="w-5 h-5 text-primary" />
                                                        )}
                                                    </button>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Selected Count */}
                                {selectedEmployees.length > 0 && (
                                    <p className="text-sm text-muted-foreground">
                                        {selectedEmployees.length} employee{selectedEmployees.length > 1 ? 's' : ''} selected
                                    </p>
                                )}
                            </div>

                            <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
                                <button
                                    onClick={() => setShowNewConversation(false)}
                                    className="px-4 py-2 text-muted-foreground hover:bg-muted rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={createConversation}
                                    disabled={selectedEmployees.length === 0 || (isGroup && !groupName.trim())}
                                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Create
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Group Settings Modal */}
            {
                showGroupSettings && selectedConversation?.is_group && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-card rounded-xl shadow-xl max-w-md w-full">
                            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                                <h3 className="text-lg font-semibold text-foreground">Group Settings</h3>
                                <button
                                    onClick={() => setShowGroupSettings(false)}
                                    className="p-1 text-muted-foreground hover:bg-muted rounded-full"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-muted-foreground mb-2">
                                        Group Members ({selectedConversation.participants?.filter(p => p.type === 'employee').length || 0})
                                    </label>
                                    <div className="border border-border rounded-lg max-h-60 overflow-y-auto">
                                        {selectedConversation.participants?.filter(p => p.type === 'employee').map((participant) => (
                                            <div
                                                key={participant.id}
                                                className="flex items-center justify-between p-3 hover:bg-muted/50"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-medium">
                                                        {getParticipantInitials(participant.name)}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-foreground">{participant.name}</p>
                                                        <p className="text-xs text-muted-foreground">{participant.email}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="px-6 py-4 border-t border-border flex justify-end">
                                <button
                                    onClick={() => setShowGroupSettings(false)}
                                    className="px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* File Preview Modal */}
            {
                previewFile && (
                    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewFile(null)}>
                        <div className="bg-card rounded-2xl max-w-4xl max-h-[90vh] w-full overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                        <FileText className="w-5 h-5 text-primary" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-semibold text-foreground truncate">{previewFile.file_name}</p>
                                        <p className="text-xs text-muted-foreground">{previewFile.file_type}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <a
                                        href={previewFile.file_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-2 text-muted-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors"
                                        title="Open in new tab"
                                    >
                                        <ExternalLink className="w-5 h-5" />
                                    </a>
                                    <a
                                        href={previewFile.file_url}
                                        download={previewFile.file_name}
                                        className="p-2 text-muted-foreground hover:text-primary hover:bg-muted rounded-lg transition-colors"
                                        title="Download"
                                    >
                                        <Download className="w-5 h-5" />
                                    </a>
                                    <button
                                        onClick={() => setPreviewFile(null)}
                                        className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Modal Content */}
                            <div className="flex-1 overflow-auto p-4 bg-muted/30 flex items-center justify-center min-h-[300px]">
                                {/\.(png|jpg|jpeg|gif|webp|svg)$/i.test(previewFile.file_name || '') ? (
                                    <img
                                        src={previewFile.file_url}
                                        alt={previewFile.file_name}
                                        className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                                    />
                                ) : /\.(mp4|webm)$/i.test(previewFile.file_name || '') ? (
                                    <video
                                        src={previewFile.file_url}
                                        controls
                                        className="max-w-full max-h-[70vh] rounded-lg shadow-lg"
                                    />
                                ) : /\.(mp3|wav)$/i.test(previewFile.file_name || '') ? (
                                    <div className="bg-card p-8 rounded-xl shadow-lg">
                                        <audio src={previewFile.file_url} controls className="w-full min-w-[300px]" />
                                    </div>
                                ) : /\.pdf$/i.test(previewFile.file_name || '') ? (
                                    <iframe
                                        src={previewFile.file_url}
                                        className="w-full h-[70vh] rounded-lg shadow-lg bg-card"
                                        title={previewFile.file_name}
                                    />
                                ) : (
                                    <div className="text-center">
                                        <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                                        <p className="text-muted-foreground mb-4">Preview not available for this file type</p>
                                        <a
                                            href={previewFile.file_url}
                                            download={previewFile.file_name}
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-colors"
                                        >
                                            <Download className="w-4 h-4" />
                                            Download File
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }
        </div >
    );
};

export default AdminMessagesPage;

